import { useSelector, useDispatch } from "react-redux";
import {
    DataGrid,
    gridClasses,
    GridToolbar,
} from '@mui/x-data-grid';

import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Checkbox,
}
    from "@mui/material";
import { styled } from '@mui/material/styles';
import React, {useEffect, useState, useMemo} from "react";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import {deleteCollection, getCollectionById, getCollections, scanComplete} from "./actions/collections";
import { setCollections } from "./reducers/collections";
import {getCustomers} from "./actions/customers";
import {setCustomers} from "./reducers/customers";
import {getAssetTypes} from "./actions/asset_types";
import {setAssetTypes} from "./reducers/asset_types";
import {useParams} from "react-router-dom";
import {setAssets, addAsset, updateAsset, deleteAsset, clearAssets} from "./reducers/assets";
import {createAsset, deleteAssetFromDb, editAssetOnDb,getSerialNumbers} from "./actions/assets";
import jwt_decode from "jwt-decode";
import {useCookies} from "react-cookie";
//import { useCSVReader } from 'react-papaparse';
//import useMediaQuery from '@mui/material/useMediaQuery';

import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from '@mui/material/Autocomplete';
import { EMPTY_STATS, statsFromCollection } from "./utilities/stats";
import { formatCityStateZip } from "./utilities/string";
import { buildScanIdentity, nextLocalScanSeq } from "./utilities/scanUid";
import { displayItemNumbers } from "./utilities/itemNumber";
import { SCAN_OUTCOME, classifyScan, serialKey } from "./utilities/scanMatch";
import { EMPTY_TALLY, recordDuplicate, duplicateCount, duplicateEvents } from "./utilities/duplicateTally";
import { playRejectTone } from "./utilities/sound";

import { useRef } from 'react';

const pageSizeOptions = [20];

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
    [`&`]: { height: '850px' },
    [`& .${gridClasses.row}.even`]: {
        backgroundColor: '#F8F8F8'
    },
    [`& .${gridClasses.row}.legacy,& .${gridClasses.row}.collected`]: {
        backgroundColor: '#c9ffde'
    },
    // Spec §2.2: an unexpected item is highlighted yellow. Declared AFTER .collected and
    // with a doubled class selector so it wins -- every unexpected asset is also collected,
    // which is why these rows have always rendered green instead.
    [`& .${gridClasses.row}.unexpected.unexpected`]: {
        backgroundColor: '#fff3bf'
    },
    // P1-3: the already-collected row a duplicate scan hit. Cleared on the next accepted
    // entry, so it marks "this is the one you just tried to scan again" and nothing more.
    [`& .${gridClasses.row}.duplicate-flash.duplicate-flash`]: {
        backgroundColor: '#ffd3d3',
        boxShadow: 'inset 4px 0 0 0 #c62828'
    },
    [`& .${gridClasses.row}.Mui-selected`]: {
        backgroundColor: '#006225',
        color: 'white'
    },
    [`& .${gridClasses.row}.Mui-selected:hover`]: {
        backgroundColor: '#009f3c',
        color: 'white'
    },
}));


const AssetManagement = (props = {}) => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const [ isLoaded, setIsLoaded ] = useState(false);

    //const theme = useTheme();
    //const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    //const { CSVReader } = useCSVReader();

    const collections = useSelector(state => state.collections.value);

    const customers = useSelector(state => state.customers.value);
    const assetTypes = useSelector(state => state.assetTypes.value);
    const assets = useSelector(state => state.assets.value);
    const auth = useSelector(state => state.auth.value);
    const states = useSelector(st => st.states.value );
    //const [cookies, setCookie, removeCookie] = useCookies(['access_token']);
    const [cookies] = useCookies(['access_token']);
    const [ newAssetId, setNewAssetId ] = useState(null);
    const [ newAssetAssetType, setNewAssetAssetType ] = useState(null);
    const [ newAssetSerialNumber, setNewAssetSerialNumber ] = useState('');
    const [ newAssetAssetTag, setNewAssetAssetTag ] = useState('');
    const [ newAssetDescription, setNewAssetDescription ] = useState('');
    const [ newAssetNotes, setNewAssetNotes ] = useState('');
    const [ selectedCollection, setSelectedCollection ] = useState(null);
    const [ collectionsWithRelations, setCollectionsWithRelations ] = useState([]);
    const [ assetsWithRelations, setAssetsWithRelations ] = useState([]);


    const [ loading, setLoading ] = useState(false);
    const [ selectionModel, setSelectionModel] = useState([]);
    // P1-15: the asset type is sticky by DEFAULT -- scanning 141 laptops should not
    // mean 141 trips to the dropdown. The checkbox is rendered inverted, as an opt-in
    // "Clear after each scan".
    const [ pinAssetType, setPinAssetType] = useState(true);
    const [ pinDescription, setPinDescription] = useState(false);
    const [ pinNotes, setPinNotes] = useState(false);
    // The type the operator explicitly picked. Kept separate from newAssetAssetType
    // because a matched scan overwrites the latter with the matched row's own type;
    // without this the pinned value would drift to whatever was last scanned.
    const [ pinnedAssetType, setPinnedAssetType] = useState('');
    const [stats , setStats ] = useState(EMPTY_STATS);
    const [dataFilter , setDataFilter ] = useState([]);
    const [openSnackbar, setSnackbar ] = useState(null);
    const [confirm, setConfirm] = useState({'open':false});
    const [overrideId, setOverrideId] = useState(null);

    // P1-3 / P1-8. The duplicate tally is a SESSION counter, deliberately not part of
    // `stats`: the other four tiles describe rows that exist and come from one
    // server-side definition (P1-2), while a duplicate is a scan that was refused and
    // persisted nowhere. See utilities/duplicateTally.js.
    const [duplicateTally, setDuplicateTally] = useState(EMPTY_TALLY);
    // A refused scan gets a blocking notice rather than the snackbar: the snackbar
    // auto-hides after 6s and has a single slot, so a following message replaces it.
    // A duplicate the operator never saw is the 03/06/2026 symptom all over again.
    const [blockingNotice, setBlockingNotice] = useState(null);
    const [showDuplicateLog, setShowDuplicateLog] = useState(false);
    // The row a duplicate scan collided with, highlighted until the next accepted entry.
    const [flashDuplicateId, setFlashDuplicateId] = useState(null);

    const handleSnackbarClose = () =>{
        setSnackbar(null);
    }

    const handleConfirmClose =()=>{
        let params = {'open':false}
        setConfirm(params)
    }

    const scanCompleteAction = async(collection)=>{
        handleConfirmClose();
        const result = await scanComplete(auth.token, collection);
        if(result.status ==='success'){
            const updatedCollections = await getCollections(auth.token);
            dispatch(setCollections(updatedCollections.data));

            setSnackbar({status:'success','message':result.message});
        }else{
            setSnackbar({status:'error','message':'Oh Snap! Something went wrong. Please try again'});
        }
    }

    const uncollectAssetConfirm = () => {
        let params = {'open':true,
            'action':uncollectAsset,
            'title':'Confirm Remove',
            'msg':'Are you sure you want to remove this item from the collected assets?',
            'btn_msg':'Yes! Remove It'}
        setConfirm(params)
    }

    const deleteTheAsset = async(id, collection_id) => {
        setConfirm({open:false});
        const result = await deleteAssetFromDb(auth.token, id);
        if( result.status === "success") {
            dispatch(deleteAsset(id));
            const collection = await getCollectionById(collection_id, auth.token);
            setStats(statsFromCollection(collection));
            setSnackbar({status:'success','message':'Well Done! Asset successfully deleted.'});
        } else {
            setSnackbar({status:'error','message':'Oh Snap! Unable to delete asset.'});
        }


        //const canDeletes = (selectedCollection != null && Date.parse(selectedCollection.start_time) > (Date.now() - 1000 * 60 * 60 * 24 * 7));
        // can delete if they are a super admin / admin / manager or if the collection is less than a week old
        /*
        const canDelete = [1,2,3,4,5,6].find(x => jwt_decode(cookies.access_token).user.role === x) || (selectedCollection != null && Date.parse(selectedCollection.start_time) > (Date.now() - 1000 * 60 * 60 * 24 * 7));
        disabled={!canDelete} ->ilagay mo ito sa button sa baba
         */

        //console.log(confirm)
    }


    const uncollectAsset = async() => {
        setConfirm({'open':false})
        let asset = assets.find(a => newAssetSerialNumber !== "" && a.serial_number === newAssetSerialNumber);
        if( !asset ){
            setSnackbar({status:'error','message':'Collection Failed. Asset not found'});
            return;
        }
        const result = await editAssetOnDb(auth.token, Object.assign({}, asset, { collected: false }));
        if (!result || result.status !== 'success') {
            setSnackbar({status: 'error', 'message': result?.message || 'Oh Snap! Unable to remove asset from collected assets.'});
            return;
        }
        dispatch(updateAsset({ ...asset, ...(result.data || {}), collected: false }));

        const collection = await getCollectionById(asset.collection_id, auth.token);
        setStats(statsFromCollection(collection));
        completeEntry();
        setSnackbar({status:'success','message':'Well Done! Asset successfully removed from collected assets!'});
    }

    // ── P1-3: one entry point for every scan, from every source ──────────────────
    //
    // Replaces `collectAsset`, which re-derived the match itself with raw `===` (so it
    // could disagree with the normalised comparison the filter effect had just done),
    // and then checked for a duplicate by looking up `newAssetId` -- form state written
    // by an effect -- rather than the row it had resolved two lines earlier. All of that
    // is now one pure call: see utilities/scanMatch.js.
    //
    // The gun, the typed Enter and the Collect button all arrive here, so they cannot
    // behave differently from one another. That is the requirement's "identical in every
    // browser" met by construction, rather than by keeping three code paths in step.
    const runScanEntry = async ({ serial, assetTag, source }) => {
        const collection = selectedCollectionRef.current;

        // The gun listener is on the document unconditionally, so scanning into a
        // finished order used to create rows even though the buttons were hidden.
        if (collection != null && collection.scan_complete === true) {
            refuseEntry({
                title: 'Scanning Complete',
                message: 'This order is marked scan complete, so no further items can be added.'
            });
            return;
        }

        const decision = classifyScan({
            serial,
            assetTag: assetTag !== undefined ? assetTag : formRef.current.assetTag,
            assets: assetsRef.current,
            collectionId: collection?.id
        });

        switch (decision.outcome) {
            case SCAN_OUTCOME.DUPLICATE:
                registerDuplicateRejection(decision, source);
                return;

            case SCAN_OUTCOME.MATCH:
                await collectMatchedAsset(decision, source);
                return;

            case SCAN_OUTCOME.UNEXPECTED:
                // The scanned serial is passed explicitly -- see saveNewAsset's note on
                // why reading it from state here would be one entry behind.
                await saveNewAsset(true, decision, source, serial);
                return;

            case SCAN_OUTCOME.AMBIGUOUS:
                // Never guess between candidates. Show them, so the operator can see what
                // the conflict actually is instead of being told to fix something invisible.
                setDataFilter({ hasFilter: true, filterType: 'ids', value: decision.candidateIds });
                refuseEntry({ title: 'More Than One Match', message: decision.message });
                return;

            case SCAN_OUTCOME.EMPTY:
            default:
                refuseEntry({ title: 'Nothing To Scan', message: decision.message });
                return;
        }
    };

    // A matched intent-list row: collect it. The body is built from the DECISION plus the
    // form refs, never from state that may have been set in this same tick.
    const collectMatchedAsset = async (decision, source) => {
        const asset = decision.asset;
        const form = formRef.current;

        // Is the editor already showing THIS row? If so the operator can see its fields and
        // any change they made to them is deliberate, so the form wins outright -- including
        // a field they deliberately emptied. (This is the "select a listed item, correct its
        // asset type, press Collect" case; taking the stored type there would silently
        // discard the correction.)
        //
        // If the editor is showing something else -- a gun or typed scan of a row that was
        // never selected -- the row's own values win, because the dropdown is then holding
        // P1-15's pinned type from the *previous* item and would overwrite the intent list's
        // answer with it. A non-empty pinned Description/Notes still applies, which is what
        // those pins are for.
        const editingThisRow = form.assetId != null && String(form.assetId) === String(asset.id);

        const newAsset = {
            id: asset.id,
            "collection_id": selectedCollectionRef.current?.id ?? selectedCollectionId,
            "collector_id": auth.id,
            "asset_type_id": editingThisRow
                ? (form.assetTypeId || asset.asset_type_id)
                : (asset.asset_type_id ?? form.assetTypeId),
            "asset_tag": editingThisRow ? form.assetTag : (form.assetTag || asset.asset_tag),
            "serial_number": asset.serial_number,
            "description": editingThisRow ? form.description : (form.description || asset.description),
            "collection_notes": editingThisRow ? form.notes : (form.notes || asset.collection_notes),
            "pending": true,
            "collected": true,
            "legacy": false,
            "make_id": asset.make_id,
            "manufacturer": asset.manufacturer,
            "model": asset.model,
            "model_id": asset.model_id,
            // P1-12: identify this scan on the device. The server records its own
            // received-at alongside it, stamps collected_timestamp, and assigns the
            // authoritative scan_number.
            ...takeScanIdentity()
        };

        const result = await editAssetOnDb(auth.token, newAsset);

        // The server refused it as a duplicate, meaning it knew something this device's
        // asset list did not -- another device collected the item first. Counted the same
        // way as a locally detected duplicate, or the two-device case stays invisible.
        if (result?.code === 'DUPLICATE_SCAN') {
            registerDuplicateRejection(
                { ...decision, duplicateOfId: result.duplicateOfId ?? asset.id },
                source,
                { fromServer: true });
            return;
        }

        if (!result || result.status !== 'success') {
            refuseEntry({
                title: 'Collection Failed',
                message: result?.message || 'Oh Snap! Collection failed. Please try again.'
            });
            return;
        }

        dispatch(updateAsset({...asset, ...newAsset, ...(result.data || {})}));

        const collection = await getCollectionById(newAsset.collection_id, auth.token);
        setStats(statsFromCollection(collection));
        completeEntry();
        setDataFilter({top: asset.id});
        setSnackbar({status: 'success', 'message': 'Well Done! Asset Collection Successful'});
    }

    // P1-8: the single place the Duplicate count moves. Exactly one increment per
    // rejected attempt -- a locally classified duplicate never reaches the network, and a
    // server-detected one never passed the classifier, so they cannot both fire.
    const registerDuplicateRejection = (decision, source, options = {}) => {
        const collectionId = selectedCollectionRef.current?.id ?? selectedCollectionId;

        setDuplicateTally(tally => recordDuplicate(tally, {
            collectionId,
            serial: decision.serialKey,
            assetTag: decision.tagKey,
            assetId: decision.duplicateOfId,
            at: new Date().toISOString(),
            source
        }));

        if (decision.duplicateOfId != null) {
            // Float the row it collided with to the top and flash it, so "already
            // collected" is a thing the operator can see rather than just be told.
            setFlashDuplicateId(decision.duplicateOfId);
            setDataFilter({top: decision.duplicateOfId});
        }

        refuseEntry({
            title: 'Duplicate Scan',
            message: decision.message || 'Duplicate Scan — this item is already collected on this order.',
            detail: options.fromServer
                ? 'Another device collected this item first.'
                : null
        });

        if (options.fromServer) {
            // Our asset list was behind. Catch it up so the tiles and row colours agree
            // with what the server just told us.
            refreshCollection();
        }
    };

    const refreshCollection = async () => {
        const collectionId = selectedCollectionRef.current?.id;
        if (collectionId == null) {
            return;
        }
        const collection = await getCollectionById(collectionId, auth.token);
        if (collection?.data?.assets != null) {
            dispatch(setAssets(collection.data.assets));
            setStats(statsFromCollection(collection));
        }
    };


    const handleSelection = (newSelection) => {
        if (newSelection) {
            if( ( newSelection.length === 1 && selectionModel.length === 1 ) && newSelection[0] === selectionModel[0]) {
                emptyNewAssetFields();
            } else {
                const selectedAsset = assets.find(a => a.id === newSelection[0]);
                if(selectedAsset){
                    setAssetEditorFields(selectedAsset.id, selectedAsset.asset_type_id, selectedAsset.serial_number, selectedAsset.asset_tag, selectedAsset.description, selectedAsset.collection_notes);
                    setSelectionModel(newSelection);
                }
            }
        } else {
            setSelectionModel([]);
        }
    }

    const handleDataFilter = (props) => {
        emptyNewAssetFields();
        setDataFilter({...props})
    }

    const handleNewAssetAssetTypeSelected = (value) => {
        setNewAssetAssetType(value);
        // P1-15: remember the operator's explicit choice so it survives resets.
        setPinnedAssetType(value);
        setDataFilter({hasFilter: true, filterType: 'asset', 'value': value})
    }

    const handleNewAssetSerialNumber = (value) => {
        setNewAssetSerialNumber(value)
        setDataFilter({hasFilter: true, filterType: 'serial', 'value': value})
    }


    const handleNewAssetTag = (value) => {
        setNewAssetAssetTag(value);
        setDataFilter({hasFilter: true, filterType: 'tag', 'value': value})
    }

    const handleSerialTyped = (value) => {
        setNewAssetAssetType(value);
        setDataFilter({hasFilter: true, filterType: 'asset', 'value': value})
    }

    // P1-12: the same Item # the exported report shows, from the same helper, so the
    // operator's screen and the customer's report can never disagree about it.
    //
    // Computed from the full `assets` slice, NOT from assetsWithRelations, which is the
    // tile-filtered subset -- deriving the fallback from a filtered list would make the
    // numbers shift every time someone clicked a stat tile.
    //
    // Rows imported before this feature shipped have no stored item_number and are not
    // backfilled (see migration 002). They get a stable display index assigned after the
    // highest real Item #, which is a display position, not an intent-list position.
    const itemNumbers = useMemo(() => displayItemNumbers(assets), [assets]);

    const columns = [
        {
            field: "edit-action",
            width: 20,
            headerName: "",
            sortable: false,
            renderCell: (params) => {
                const onClick = (e) => {
                    setAssetEditorFields(params.row.id, params.row.asset_type_id, params.row.serial_number, params.row.asset_tag, params.row.description, params.row.notes);
                };

                const canEdit = [1,2,3].find(x => jwt_decode(cookies.access_token).user.role === x) || (selectedCollection != null && Date.parse(selectedCollection.start_time) > (Date.now() - 1000 * 60 * 60 * 24 * 7));

                return <IconButton variant="outlined" onClick={onClick} disabled={!canEdit}>
                    <EditIcon/>
                </IconButton>
            }
        },
        { field: 'id', headerName: 'ID', width: 100, editable: false},
        // P1-12: Item # is the row's position in the intent list, not the database id.
        { field: 'item_number', headerName: 'Item #', width: 90, editable: false, type: 'number',
            valueGetter: (params) => itemNumbers.get(params.row.id) ?? null,
            renderCell: (params) => params.value == null ? '—' : params.value },
        { field: 'scan_uid', headerName: 'Scan UID', width: 240, editable: false },
        { field: 'asset_type', headerName: 'Asset Type', width: 200, editable: false, valueGetter: (params) => params.row.assetType != null ? params.row.assetType.name : ""},
        { field: 'manufacturer', headerName: 'Make', width:150 },
        { field: 'model', headerName: 'Model', width: 300 },
        { field: 'serial_number', headerName: 'Serial Number', width: 150, editable: false},
        { field: 'asset_tag', headerName: 'Asset Tag', width: 125, editable: false },
        { field: 'description', headerName: 'Description', width:125, editable: false },
        { field: 'collection_notes', headerName: 'Notes', width: 300, editable: false },



        { field: 'comments', headerName: 'Comments', width: 300, editable: false },
        /*
        {
            field: "collected",
            headerName: "Collected",

            sortable: false,
            renderCell: (params) => {
                return true;
                if( params.row.collected || params.row.legacy ) {
                    return "True"
                } else {
                    return "False"
                }
            }
        },
*/

        {
            field: "delete-action",
            headerName: "",
            width: 50,
            sortable: false,
            renderCell: (params) => {
                const onClick = async (e) => {
                    e.stopPropagation();
                    let pars = {open: true,
                        title:'Confirm Delete',
                        msg:'Are you sure you want to permanently delete this asset? This action cannot be undone.',
                        btn_msg:'Yes! Do it',
                        action: () => deleteTheAsset(params.row.id, params.row.collection_id)
                    };
                    setConfirm(pars)
                };
                //const canDelete = [1,2,3].find(x => jwt_decode(cookies.access_token).user.role === x);
                const userRole = jwt_decode(cookies.access_token).user.role;
                const canDelete = [1, 2, 3].includes(userRole) || params.row.unexpected === true;
                //const canDelete = params.row.unexpected === true;
                if(canDelete) {
                    return <IconButton variant="outlined" onClick={onClick}>
                        <DeleteIcon/>
                    </IconButton>
                }

            }
        },
    ]



    useEffect(() => {
        const fetchData = () => async (dispatch) => {
            dispatch(clearAssets());
            if (customers.length === 0) {
                const updatedCustomers = await getCustomers(auth.token);
                dispatch(setCustomers(updatedCustomers.data));
            }
            if (assetTypes.length === 0) {
                const updatedAssetTypes = await getAssetTypes(auth.token);
                dispatch(setAssetTypes(updatedAssetTypes.data));
            }
            if (collections.length === 0) {
                const updatedCollections = await getCollections(auth.token);
                dispatch(setCollections(updatedCollections.data));
            }
            setIsLoaded(true);
        }
        dispatch(fetchData());
    }, [dispatch,assetTypes,auth,collections,customers] );


    useEffect(() => {
        setCollectionsWithRelations(isLoaded ? collections
            .map( collection => {
                //wag kana mangealam dito
                const cust = customers.find( customer => customer.id === collection.customer_id);
                return Object.assign({},
                    collection,
                    {
                        customer: cust || { } ,
                        // cust is optional above, so it has to be optional here too.
                        state: states.find(s => s.id === cust?.state_id)
                    } );
            })
            .sort((a,b) => {
                if (a.customer.customer_name !== b.customer.customer_name) {
                    // customer_name is missing whenever cust fell back to {} above.
                    return (a.customer.customer_name || '').localeCompare(b.customer.customer_name || '')
                }
                if (a.lot_number !== b.lot_number) {
                    return parseInt(a.lot_number,10) - parseInt(b.lot_number, 10);
                }
                return 0;
            }) : []);

    }, [collections, isLoaded,customers,states]);

    // Filter assetsWithRelations — WHICH ROWS TO SHOW, and nothing else.
    //
    // ⚠ P1-3: this effect used to also DECIDE things. Its serial branch called
    // guardedCollect() as a side effect, but only when `dataFilter.entered === 1` -- the
    // typed-Enter flag. A gun scan set `collect:1` and never `entered:1`, so a gun scan
    // that matched zero rows, or two, fell through the else below and silently cleared
    // four form fields: no save, no message, nothing. That was the single largest hole in
    // duplicate detection, because a duplicate that failed to resolve was simply invisible.
    //
    // Deciding now happens in runScanEntry, synchronously, from a pure classifier. Keep
    // this effect display-only: no collecting, no saving, no `collect`/`entered` flags.
    useEffect(() => {
        const enrichedAssets = isLoaded ? assets
            .map(asset => Object.assign({}, asset, {
                collection: collectionsWithRelations.find(collection => collection.id === asset.collection_id),
                assetType: assetTypes.find(assetType => assetType.id === asset.asset_type_id),
            })) : [];
        let filteredAssets = enrichedAssets;
        if(dataFilter.hasFilter){
            if(dataFilter.filterType ==='stat'){
                // P1-2: these must mirror CC_Backend/utils/stats.js exactly, or a
                // tile's number won't match the rows you get when you click it.
                // collected/unexpected are nullable, so never compare to false.
                if(dataFilter.value === 'unexpected'){
                    filteredAssets = enrichedAssets.filter(asset => asset.unexpected === true);
                }else if(dataFilter.value ==='unscanned'){
                    filteredAssets = enrichedAssets.filter(asset => asset.unexpected !== true && asset.collected !== true);
                }else if(dataFilter.value ==='matched'){
                    filteredAssets = enrichedAssets.filter(asset => asset.unexpected !== true && asset.collected === true);
                }else{
                    filteredAssets = enrichedAssets.filter(asset => asset.unexpected !== true);
                }
            }else if(dataFilter.filterType ==='asset'){
                filteredAssets = enrichedAssets.filter(asset => asset.asset_type_id === dataFilter.value);
            }else if(dataFilter.filterType ==='ids'){
                // P1-3: show exactly these rows. Used when a scan was AMBIGUOUS, so the
                // operator can see the conflicting rows rather than being told to fix
                // something they cannot see.
                const ids = Array.isArray(dataFilter.value) ? dataFilter.value : [];
                filteredAssets = enrichedAssets.filter(asset => ids.includes(asset.id));
            }else if(dataFilter.filterType ==='tag'){
                var tag = dataFilter.value;
                filteredAssets = enrichedAssets.filter(asset => asset.asset_tag?.includes(tag));

                if(filteredAssets.length===1 && filteredAssets[0].asset_tag===tag ){
                    let selectedAsset = filteredAssets[0];
                    setAssetEditorFields(selectedAsset.id, selectedAsset.asset_type_id, selectedAsset.serial_number, selectedAsset.asset_tag, selectedAsset.description, selectedAsset.collection_notes);
                    setSelectionModel([selectedAsset.id]);
                }
            }else if(dataFilter.filterType ==='serial'){
                var serial = dataFilter.value;
                // Through serialKey, so the grid narrows on exactly the identity the
                // classifier matched on. Two different normalisations here and there is how
                // the old code could show a row it then refused to collect.
                const wanted = serialKey(serial);
                filteredAssets = enrichedAssets.filter(asset => serialKey(asset.serial_number) === wanted);

                if(filteredAssets.length===1){
                    // Populate the editor so the operator can see and amend what matched.
                    // It does NOT collect any more -- that is runScanEntry's job.
                    let selectedAsset = filteredAssets[0];
                    setAssetEditorFields(selectedAsset.id, selectedAsset.asset_type_id, selectedAsset.serial_number, selectedAsset.asset_tag, selectedAsset.description, selectedAsset.collection_notes);
                    setSelectionModel([selectedAsset.id]);
                }else{
                    // Zero or several matches: there is no single row to show in the editor.
                    // Previously this branch also swallowed every unresolved gun scan.
                    setSelectionModel([]);
                    setNewAssetId(null);
                    setNewAssetAssetTag('');
                    setNewAssetDescription('');
                    setNewAssetNotes('');
                }
            }
        }
        setAssetsWithRelations(filteredAssets);
    }, [assets, assetTypes, collectionsWithRelations, isLoaded, dataFilter, newAssetAssetType]);

    useEffect(() => {
        const the_id = overrideId !== null ? parseInt(overrideId) : id ? parseInt(id) : null;
        if (the_id !== null) {
            setSelectedCollection(collectionsWithRelations.find( collection => collection.id === the_id))
        }
        /*
        if( id != null && id !== '' ) {
            setSelectedCollection(collectionsWithRelations.find( collection => collection.id === parseInt(id)))
        }
         */
        console.log('effect 575')

    }, [ collectionsWithRelations, isLoaded,id]);


    // P1-12: the device's own view of the scan sequence, used only to fill the
    // trailing segment of the scan uid. The authoritative scan_number is assigned by
    // the server, which is the only party that can see two devices on one order.
    // Re-seeded whenever a collection is opened so a fresh device continues the
    // sequence rather than restarting it at 1.
    const scanSeq = useRef(1);

    useEffect(() => {
        setLoading(true);
        selectedCollectionRef.current = selectedCollection;
        const fetchData = async() => {
            if (selectedCollection != null) {
                const collection = await getCollectionById(selectedCollection.id, auth.token);
                dispatch(setAssets(collection.data.assets));
                setStats(statsFromCollection(collection));
                scanSeq.current = nextLocalScanSeq(collection.data.assets);
            }
            setLoading(false);
        };
        fetchData();
    }, [ selectedCollection,auth,dispatch]);

    // P1-8: the duplicate tally belongs to one order. Opening a different collection
    // starts a fresh one rather than carrying a stale number across -- the tally itself
    // enforces this (it stores its own collection id), so this only clears the UI that
    // hangs off it.
    useEffect(() => {
        setDuplicateTally(EMPTY_TALLY);
        setFlashDuplicateId(null);
        setShowDuplicateLog(false);
    }, [selectedCollection?.id]);

    // Mint the identity for the scan about to be sent. The lot number is the order
    // number the client knows the job by, so it is what goes in the uid.
    const takeScanIdentity = () => {
        const identity = buildScanIdentity(selectedCollection?.lot_number, scanSeq.current);
        scanSeq.current += 1;
        return identity;
    };




    // ── P1-3: the scan buffer, and the actual cause of the Edge/Chrome divergence ──
    //
    // These were `useState` (scanBuffer, lastKeyTime, scannedData) and the listener was
    // re-registered on [scanBuffer, lastKeyTime]. Under React 18's automatic batching --
    // which applies to native event handlers too -- the values read inside the handler
    // are whatever was last COMMITTED, not what the previous keystroke just set. So
    // within one burst from the gun:
    //
    //   * `lastKeyTime` was still the pre-burst value for every keystroke React hadn't
    //     committed, so `timeSinceLastKey > 100` kept coming out true and the buffer was
    //     RESET on each character instead of appended to;
    //   * `scanBuffer.length` on Enter was a stale read, so a complete scan could look
    //     short and fall into the "Barcode too short" branch -- which was never
    //     rendered, so it vanished silently.
    //
    // Whether a commit lands between two HID keystrokes is scheduler- and
    // browser-dependent. That is what "the counts weren't working in Edge" was: not a
    // count bug at all, a truncated or discarded serial. Refs make the accumulation
    // synchronous, so it cannot depend on render timing, and the listener is registered
    // once. Nothing renders the buffer, so state bought nothing and cost determinism.
    const scanBufferRef = useRef('');
    const lastKeyTimeRef = useRef(0);

    const SCAN_KEY_GAP_MS = 100;
    const MIN_BARCODE_LENGTH = 5;

    // ── Latest-value refs ────────────────────────────────────────────────────────
    // The scan decision and the request body it produces are built in ONE synchronous
    // pass, from these, rather than from state that may have been set in the same tick.
    //
    // That ordering discipline is the general form of two bugs already fixed by hand
    // here: `collectAsset` reading `newAssetId` (form state written by an effect) instead
    // of the row it had just resolved, and P1-17's clear-then-immediately-refilled form.
    // Reading through a ref makes "the value as of now" unambiguous.
    const assetsRef = useRef(assets);
    const selectedCollectionRef = useRef(null);
    const formRef = useRef({});
    // The listener below is registered once, so it must not close over the first
    // render's callback. It calls through this instead.
    const runScanEntryRef = useRef(() => {});

    useEffect(() => {
        assetsRef.current = assets;
    }, [assets]);

    useEffect(() => {
        formRef.current = {
            assetId: newAssetId,
            assetTypeId: newAssetAssetType,
            assetTag: newAssetAssetTag,
            serialNumber: newAssetSerialNumber,
            description: newAssetDescription,
            notes: newAssetNotes
        };
    }, [newAssetId, newAssetAssetType, newAssetAssetTag, newAssetSerialNumber,
        newAssetDescription, newAssetNotes]);

    // The scanner is a document-level listener, so a keystroke typed into a form field
    // reaches BOTH the field and this handler. With focus in Serial #, two scans in a
    // row were concatenated into one value -- an asset was saved with the serial
    // "BLIND-AAA111BLIND-BBB222". Ignoring events that are already going to an input
    // costs nothing: the Serial # field has its own onKeyDown, so typed entry still
    // works exactly as before.
    //
    // Added by P1-12, because scan_number and scan_uid are only meaningful if one
    // physical scan produces one scan event.
    const isTypingTarget = (target) => {
        if (!target) return false;
        const tag = (target.tagName || '').toLowerCase();
        return tag === 'input' || tag === 'textarea' || tag === 'select'
            || target.isContentEditable === true;
    };

    // Registered ONCE. Everything it needs is behind a ref, so it never goes stale and
    // never has to be torn down and rebuilt between keystrokes.
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (isTypingTarget(e.target)) {
                return;
            }

            if (e.key && e.key.length === 1 && e.key !== ' ' && !e.ctrlKey && !e.altKey && !e.metaKey) {
                const now = Date.now();
                // A real gap means a new scan; anything faster is the same burst.
                scanBufferRef.current = (now - lastKeyTimeRef.current > SCAN_KEY_GAP_MS)
                    ? e.key
                    : scanBufferRef.current + e.key;
                lastKeyTimeRef.current = now;
            }

            if (e.key === 'Enter') {
                const scanned = scanBufferRef.current;
                scanBufferRef.current = '';

                if (scanned.length > 0) {
                    if (scanned.length >= MIN_BARCODE_LENGTH) {
                        // Show it, then decide. The decision reads the value passed in,
                        // not this state, so the two cannot disagree.
                        setNewAssetSerialNumber(scanned);
                        runScanEntryRef.current({ serial: scanned, source: 'gun' });
                    } else {
                        // Previously `setError(...)` into state nothing rendered, so a
                        // short read was completely silent -- the operator saw a scan
                        // that simply did nothing.
                        setSnackbar({ status: 'warning', message: 'Barcode too short — please scan again.' });
                    }
                }
                e.preventDefault(); // Prevent form submission if in a form
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const collectionInProgress = selectedCollection == null || selectedCollection.end_time == null || selectedCollection.end_time === '' ;
    const selectedCollectionId = selectedCollection == null ? '' : selectedCollection.id;


    const handleAutoSave = () =>{
        if(newAssetId){
            editAsset();
        }
    }

    const editAsset = async () => {
        const currentAsset = assets.find(a => a.id === newAssetId);
        //console.log(currentAsset)
        const newAsset ={
            id: newAssetId,
            "collection_id": selectedCollectionId,
            "collector_id": auth.id,
            "asset_type_id": newAssetAssetType,
            "asset_tag": newAssetAssetTag,
            "serial_number": newAssetSerialNumber,
            "description": newAssetDescription,
            "collection_notes": newAssetNotes,
            "pending": true,
            "collected": currentAsset.collected,
            "legacy": false,
            "make_id":currentAsset.make_id,
            "manufacturer": currentAsset.manufacturer,
            "model": currentAsset.model,
            "model_id": currentAsset.model_id,
        };

        const result = await editAssetOnDb(auth.token, newAsset);

        if( result.status === "success") {
            setSnackbar(result);
            dispatch(updateAsset(newAsset));
            // P1-17: this path cleared nothing at all, so after an edit the form stayed
            // populated with the row just saved and the next entry had to be typed over it.
            completeEntry();
        } else {
            // P1-3: a failed edit is a refusal like any other, so it gets the same visible
            // treatment rather than a snackbar that may already have been replaced.
            //
            // This path carries NO scan identity (it sends the row's current `collected`),
            // so the server classifies it as an edit and it can never be refused as a
            // duplicate -- that is what keeps P1-1's notes-on-a-collected-asset fix working.
            refuseEntry({
                title: 'Update Failed',
                message: result?.message || 'Oh Snap! Update Failed!'
            });
        }
    }

    // P1-3: needs NO duplicate check of its own. A scan whose serial is already collected
    // on this order -- including one that was itself added on site as unexpected -- is
    // classified DUPLICATE by runScanEntry and never reaches here. The old code had no
    // check at all on this path, which is how a re-scan created a second physical row.
    //
    // `decision` is passed through only so a server-side rejection can be attributed to the
    // same scan; the rest of the payload comes from the form, because for an unexpected item
    // the form IS the record.
    //
    // ⚠ `scannedSerial` must be passed on the scan paths. A gun scan calls
    // setNewAssetSerialNumber() and then classifies SYNCHRONOUSLY, so reading
    // `newAssetSerialNumber` here would get the PREVIOUS entry's value -- the same
    // same-tick staleness that broke the scan buffer. It used to be safe only because the
    // old flow detoured through an effect, which forced a render in between. The "Add
    // Unexpected Asset" button has no scan to pass and falls back to the form.
    const saveNewAsset = async (collected=false, decision=null, source=null, scannedSerial=null) => {
       const enteredSerial = String(scannedSerial ?? newAssetSerialNumber ?? '');

       if(!newAssetAssetType ) {
            // P1-17: this is the likeliest cause of the reported symptom. pinAssetType
            // defaults on while pinnedAssetType starts empty, so unless the operator has
            // used the dropdown, every clear writes '' back into the type -- and the next
            // manually typed entry for an item that isn't on the intent list lands here and
            // returned before ever reaching the clear below, leaving the serial in place.
            refuseEntry({title: 'Asset Type Required', message: 'Oh Snap! Please select an Asset Type.'});
            return false;
        }else if(!enteredSerial.trim()){
            refuseEntry({title: 'Serial Number Required', message: 'Oh Snap! Please provide a Serial Number.'});
            return false;
        }else{
            const newAsset =         {
                "collection_id": selectedCollectionRef.current?.id ?? selectedCollectionId,
                "collector_id": auth.id,
                "asset_type_id": (newAssetAssetType > 0 ? newAssetAssetType : 10),
                "asset_tag": newAssetAssetTag,
                "serial_number": enteredSerial.trim(),
                "description": newAssetDescription,
                "collection_notes": newAssetNotes,
                "pending": true,
                "collected": collected,
                "legacy": false,
                // P1-12: collected_timestamp used to be built here as
                // toISOString().slice(...) -- a UTC reading written into a
                // timestamp-without-time-zone column, i.e. the wrong hour. The server
                // stamps it now. What the device contributes is the scan's identity,
                // and only when something was actually scanned.
                ...(collected ? takeScanIdentity() : {}),
            };

            const result = await createAsset(auth.token, newAsset);

            // P1-3: the server's own per-order guard caught something this device could
            // not see -- another operator collected or imported the serial in between.
            if (result?.code === 'DUPLICATE_SCAN') {
                registerDuplicateRejection(
                    { ...(decision || {}), serialKey: serialKey(newAsset.serial_number),
                      tagKey: '', duplicateOfId: result.duplicateOfId ?? null,
                      message: result.message },
                    source, { fromServer: true });
                return false;
            }

            // The serial IS on this order's list, just not collected -- so this device's
            // asset list was stale and the scan should have been a match, not a new row.
            // Refreshed and refused rather than quietly collected: silently turning an
            // "unexpected" decision into a "match" would hide a real client-side problem.
            if (result?.code === 'SERIAL_ON_ORDER') {
                await refreshCollection();
                refuseEntry({
                    title: 'Already On This Order',
                    message: result.message || 'That serial is on this order\'s list.',
                    detail: 'The list has been refreshed — please scan it again.'
                });
                return false;
            }

            if( result.status === "success") {
                // P1-17: clear the form and the serial filter BEFORE the new row reaches the
                // asset list -- this ordering is the actual bug behind the report.
                //
                // The filter effect repopulates the editor whenever exactly one asset matches
                // the active serial filter. Before the save that filter matched nothing, so
                // typing was safe; the moment the newly created row lands in `assets` it
                // matches, and the effect writes its serial straight back into the field. The
                // clear ran, then was immediately undone. Clearing the filter first removes
                // the window entirely.
                completeEntry();

                const collection = await getCollectionById(selectedCollectionId, auth.token);
                setStats(statsFromCollection(collection));

                dispatch(addAsset(Object.assign({}, newAsset, {id: result.id})));
                dispatch(setAssets(collection.data.assets));
                // Match what collectAsset already does. This path saved silently -- no
                // confirmation and no float-to-top -- so adding an unexpected asset read as
                // "nothing happened" even when it had worked.
                setDataFilter({top: result.id});
                setSnackbar({status: 'success', 'message': (collected
                    ? 'Well Done! Unexpected asset successfully collected.'
                    : 'Well Done! Unexpected asset successfully created.')});

            }else{
                refuseEntry({
                    title: 'Save Failed',
                    message: result?.message || 'Oh Snap! Error saving asset.'
                });
            }

        }
    }

    const isCollecting = useRef(false);

    // P1-17 introduced this guard so two fast Enters could not double-submit.
    //
    // P1-3 generalises it to wrap the whole scan entry (classification included) and --
    // importantly -- makes it AUDIBLE. It used to `return` silently, so an item scanned
    // while a slow save was still in flight was dropped with no cue at all: no row, no
    // message, and if that scan was a duplicate, no count either. On a warehouse
    // connection that is a lost asset, not a rare edge case.
    const guardedRun = (run) => {
        if (isCollecting.current) {
            setSnackbar({
                status: 'warning',
                message: 'Still saving the previous scan — please scan that item again.'
            });
            return;
        }
        isCollecting.current = true;
        Promise.resolve()
            .then(run)
            .finally(() => { isCollecting.current = false; });
    };

    // Keeps the once-registered document listener calling the CURRENT closure rather than
    // the first render's, without re-attaching it on every keystroke (which is what made
    // the old buffer non-deterministic in the first place).
    useEffect(() => {
        runScanEntryRef.current = (args) => guardedRun(() => runScanEntry(args));
    });

    // P1-3: `collect` is gone. This used to fire a collect as a side effect of the filter
    // effect writing form fields, which is how the decision ended up depending on render
    // timing and selection state. Setting the editor fields now only ever sets fields.
    const setAssetEditorFields = (id, type, number, tag, description, notes) => {
        setNewAssetId(id);
        setNewAssetAssetType(type);
        setNewAssetSerialNumber(number);
        setNewAssetAssetTag(tag);
        setNewAssetDescription(description);
        setNewAssetNotes(notes);
    }

    // P1-15: reads the pin state directly rather than taking it as arguments.
    // The old signature (pinNotes, pinDescription, pinAssetType) shadowed the outer
    // pin state, so the seven call sites that passed nothing silently cleared
    // everything, and onClick={emptyNewAssetFields} handed React's MouseEvent in as
    // the first flag. pinNotes was accepted but never read at all, which is why the
    // Notes "Keep Value" checkbox did nothing.
    const emptyNewAssetFields = () => {
        setAssetEditorFields(
            null,
            pinAssetType ? pinnedAssetType : '',
            '',
            '',
            pinDescription ? newAssetDescription : '',
            pinNotes ? newAssetNotes : ''
        );
        setSelectionModel([]);
        setDataFilter([])
    }

    // P1-17: every entry now ends in exactly one of these two, so what gets cleared no
    // longer depends on which of the five routes the operator happened to take (scan,
    // Enter with a match, Enter without one, Collect, Add Unexpected Asset, Edit Asset).
    // Reported by CompuCycle as "the serial stays in the field when typed manually", but
    // the underlying issue was that clearing was copy-pasted into some success branches
    // and absent from others -- Edit Asset never cleared at all, and no rejection path
    // cleared or signalled anything.

    // Accepted: blank the form for the next entry. Focus is deliberately NOT moved here.
    // On the typed path the cursor is already in Serial # (its onKeyDown preventDefaults
    // and never blurs), so clearing is enough; and focusing that input would make the
    // document-level scan listener ignore the scanner's keystrokes (P1-12's focus guard),
    // silently rerouting every barcode onto the typed code path.
    const completeEntry = () => {
        emptyNewAssetFields();
        // The duplicate highlight and its notice describe the previous, refused entry.
        // Once one is accepted they are stale, so a driver can keep scanning without
        // having to dismiss anything.
        setFlashDuplicateId(null);
        setBlockingNotice(null);
    };

    // Refused: keep what was entered so a typo stays visible and correctable, but select
    // it so the next keystroke -- or the next scan -- replaces it with nothing to delete.
    const serialInputRef = useRef(null);
    const rejectEntry = () => {
        const input = serialInputRef.current;
        if (input && typeof input.select === 'function') {
            input.select();
        }
    };

    // P1-3: the one way an entry is refused. Every rejection path goes through this, so a
    // refusal cannot be silent -- which is what the 03/06/2026 test call actually hit:
    // a duplicate that notified in one browser and not in another.
    //
    // Three channels, deliberately: a notice that does NOT auto-hide (the snackbar's
    // single slot and 6s timeout are why a refusal could be missed), a distinct tone for
    // an operator looking at the pallet rather than the screen, and the P1-17 behaviour of
    // keeping the serial selected so the next scan overwrites it with nothing to delete.
    const refuseEntry = ({ title, message, detail = null }) => {
        setBlockingNotice({ title, message, detail });
        playRejectTone();
        rejectEntry();
        // The grid keeps whatever filter the refusal set (candidate rows for an ambiguous
        // scan, the collided row for a duplicate); it is cleared by the next accepted entry.
    };


    const top100Films = collectionsWithRelations.map(c => ({
        label: `${c.lot_number} - ${c.customer.customer_name} - ${(c.start_time || "").slice(0, 10)}`,
        id: c.id
    }));


    return (
        <>
            <Box sx={{ height: 'auto', width: '100%' }}>
                <div style={{marginTop: 10,display:'flex', width:'100%', justifyContent:'space-between', marginBottom:15}}>
                    <div>
                        <Autocomplete
                            style={{marginRight: 10, width: 450}}
                            disablePortal
                            label="Collections"
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            options={top100Films}
                            getOptionLabel={(option) => option.label}
                            sx={{ width: 300 }}
                            renderInput={(params) => <TextField {...params} />}
                            onChange={(event, value) => {
                                if(value===null){
                                    emptyNewAssetFields();
                                    setOverrideId(0);
                                    setSelectedCollection(collectionsWithRelations.find( cwr => cwr.id === 0));
                                }else{
                                    let val = value?.id
                                    emptyNewAssetFields();
                                    setOverrideId(val);
                                    setSelectedCollection(collectionsWithRelations.find( cwr => cwr.id === val))
                                }
                            }}
                        />
                    </div>


                    {selectedCollection != null  && selectedCollection.scan_complete === false? (
                    <div>
                        <Button
                            tabIndex={-1}
                            id={'scan_complete_btn'}
                            onClick={(e) => setConfirm({
                                'open': true,
                                'action': () => scanCompleteAction(selectedCollection),
                                'title': 'Confirm',
                                'msg': 'Are you sure you want to complete scanning of this collection?',
                                'btn_msg': 'Yes! Complete Scanning'
                            })}
                            style={{marginLeft: 'auto'}}
                            variant="contained"
                            color="error"
                            disabled={false}
                        >
                            Scan Complete
                        </Button>

                    </div>) : null

                    }
                </div>

                {selectedCollection != null ? (
                    <>
                        <div id="the-collection" className="d-flex">
                            {/* P1-8: five tiles now, so the split is flexible rather than 60/40 —
                                five fixed tiles did not fit a 1366-wide driver laptop. */}
                            <div className="lefty" style={{flex: '1 1 auto', minWidth: 0}}>
                                <div className="d-flex" style={{'gap':30}}>
                                    <div>
                                        <h2>ORDER# {selectedCollection.lot_number}</h2>

                                        <div className="date">
                                            {selectedCollection.scan_complete === true ? (
                                                <>
                                                   {selectedCollection.start_time?.slice(0, 10)} - {selectedCollection.scan_complete_date?.slice(0, 10)}
                                                    <span>SCAN COMPLETED</span>
                                                </>
                                            ) : (
                                                <>
                                                 {selectedCollection.start_time?.slice(0, 10)} - {collectionInProgress ? 'In Progress' : selectedCollection.end_time?.slice(0, 10)}
                                                </>
                                            )}
                                        </div>


                                    </div>
                                    <div className="col-4" style={{marginLeft: 80}}>
                                        <h3 style={{marginTop: 0}}>{selectedCollection.customer.customer_name || ''}</h3>
                                        <address>
                                            {selectedCollection.customer.address1 || ''}<br/>
                                            {(selectedCollection.customer.address2 != null && selectedCollection.customer.address2.trim() !== '') ?
                                                <>{`${selectedCollection.customer.address2}`}<br/></> : null}
                                            {formatCityStateZip(selectedCollection.customer.city, selectedCollection.state?.code, selectedCollection.customer.zip_code)}<br/>
                                        </address>
                                    </div>
                                </div>
                            </div>
                            <div className="righty" style={{flex: '0 0 auto', position: 'relative'}}>


                                <div className="stat-tiles">
                                    <div className={`boxed-content-stats ${dataFilter.value==='expected'?'active' : ''}`}
                                         onClick={() => handleDataFilter({hasFilter: true, filterType: 'stat', value: 'expected'})}>
                                        <h2>{stats.expected}</h2>
                                        <p><strong>Expected</strong> <br/>Asset Count</p>
                                    </div>
                                    <div className={`boxed-content-stats ${dataFilter.value==='matched'?'active' : ''}`}
                                         onClick={() => handleDataFilter({hasFilter: true, filterType: 'stat', value: 'matched'})}>
                                        <h2>{stats.matched}</h2>
                                        <p><strong>Matched</strong> <br/> Asset Count</p>
                                    </div>

                                    <div className={`boxed-content-stats ${dataFilter.value==='unscanned'?'active' : ''}`}
                                         onClick={() => handleDataFilter({hasFilter: true, filterType: 'stat', value: 'unscanned'})}>
                                        <h2>{stats.unscanned}</h2>
                                        <p><strong>Unscanned</strong> <br/>Asset Count</p>
                                    </div>

                                    <div className={`boxed-content-stats ${dataFilter.value==='unexpected'?'active' : ''}`}
                                         onClick={() => handleDataFilter({hasFilter: true, filterType: 'stat', value:'unexpected'})}>
                                        <h2>{stats.unexpected} </h2>
                                        <p><strong>Unexpected</strong> <br/>Asset Count</p>
                                    </div>

                                    {/* P1-8: the fourth count the requirements ask for (spec §2.1).
                                        Deliberately NOT sourced from `stats`, which is the server's
                                        canonical shape and describes rows that exist — a duplicate is a
                                        refused scan and is persisted nowhere. See utilities/duplicateTally.js.

                                        Clicking it opens a log, and deliberately does NOT filter the grid:
                                        three attempts on one row would show one row against a count of
                                        three, which is exactly the tile/row disagreement P1-2 fixed. */}
                                    <div className="boxed-content-stats duplicate"
                                         title="Duplicate scans refused during this session"
                                         onClick={() => {
                                             if (duplicateCount(duplicateTally, selectedCollection.id) > 0) {
                                                 setShowDuplicateLog(true);
                                             }
                                         }}>
                                        <h2>{duplicateCount(duplicateTally, selectedCollection.id)}</h2>
                                        <p><strong>Duplicate</strong> <br/>Scans Refused</p>
                                    </div>
                                </div>
                            </div>
                        </div>




                        <div style={{display: "flex", flexDirection: "column", padding: 20, marginTop: 1, backgroundColor: '#F3F4F6'}}>


                            <div style={{display: "flex", flexDirection: "row"}}>

                                {/* P1-15: green while the type is sticky, so the state is visible. */}
                                <div style={{display: "flex", flexDirection: "column", marginLeft: 5, paddingLeft: 5, paddingRight: 5, backgroundColor: pinAssetType ? "#d6ffe5" : "white"}}>
                                    <TextField
                                        style={{marginTop: 10, width: 200, backgroundColor:'white'}}
                                        id="asset-type-select"
                                        value={newAssetAssetType || ''}
                                        label="Asset Type"
                                        select
                                        onChange={(e) => handleNewAssetAssetTypeSelected(e.target.value)}

                                    >
                                        {assetTypes.map(at => <MenuItem key={at.id} value={at.id}>{at.name}</MenuItem>)}
                                    </TextField>

                                    {/* Inverted: sticky is the default, so this is an opt-out. */}
                                    <FormControlLabel style={{marginLeft: 0}} control={<Checkbox sx={{color: "#006225"}} checked={!pinAssetType}/>} label={"Clear after each scan"} onChange={(event) => setPinAssetType(!pinAssetType)}/>
                                </div>

                                <div>
                                    {/* inputRef is P1-17: rejectEntry() selects this field's text so a
                                        refused entry can be overwritten without deleting it first. */}
                                    <TextField onChange={(e) => handleNewAssetSerialNumber(e.target.value)}
                                               onKeyDown={(e) => {
                                                   if (e.key === 'Enter'){
                                                       e.preventDefault();
                                                       // P1-3: read e.target.value, NOT newAssetSerialNumber.
                                                       // When a gun scans into this focused field, the final
                                                       // character's onChange can batch with this keydown, so
                                                       // the state value can still be missing that character --
                                                       // the same staleness that broke the scan buffer. The DOM
                                                       // node always has the complete value.
                                                       runScanEntryRef.current({ serial: e.target.value, source: 'typed' });
                                                   }
                                               }}

                                               value={newAssetSerialNumber}
                                               inputRef={serialInputRef}

                                               style={{marginLeft: 10, marginTop: 10, width: 200, backgroundColor:'white'}}
                                               id="new-asset-serial-number" label="Serial #"
                                               variant="outlined" />
                                </div>

                                <div>
                                    <TextField onChange={(e) => handleNewAssetTag(e.target.value)} style={{marginLeft: 10, marginTop: 10, width: 200, backgroundColor:'white'}} id="new-asset-asset-tag" label="Asset Tag" variant="outlined" value={newAssetAssetTag}/>
                                </div>

                                {/*
                            <div style={{ display: "flex", flexDirection: "column", flex: 1, marginLeft:5, paddingLeft: 5, paddingRight:5, backgroundColor: pinDescription ? "#d6ffe5" : "white" }}>
                                <TextField onChange={(e) => setNewAssetDescription(e.target.value)} style={{flex: 1, marginTop: 10, backgroundColor: "white"}} id="new-asset-description" label="Description" variant="outlined" value={newAssetDescription} />
                                <FormControlLabel style={{marginLeft: 0}} control={<Checkbox sx={{color: "#006225"}} checked={pinDescription}  />} label={"Keep Value"} onChange={(event) => setPinDescription(!pinDescription)}/>
                            </div>
                             */}

                                {/* P1-15: the Notes pin now actually persists the value, so show it too. */}
                                <div style={{display: "flex", flexDirection: "column", flex: 1, marginLeft: 5, paddingLeft: 5, paddingRight: 5, backgroundColor: pinNotes ? "#d6ffe5" : "white"}}>
                                    <TextField
                                        onChange={(e) => {
                                            setNewAssetNotes(e.target.value);
                                            //handleAutoSave();
                                        }}

                                        onBlur={(e)=>handleAutoSave()}

                                        style={{flex: 1, marginLeft: 10, marginTop: 10, backgroundColor:"white"}}
                                        id="new-asset-notes"
                                        label="Notes"
                                        variant="outlined"
                                        value={newAssetNotes}/>

                                    <FormControlLabel style={{marginLeft: 0}} control={<Checkbox sx={{color: "#006225"}} checked={pinNotes}/>} label={"Keep Value"} onChange={(event) => setPinNotes(!pinNotes)}/>
                                </div>

                            </div>
                            <div id="action_btns">

                                {selectedCollection.scan_complete === false ? (
                                    <>
                                        {/* P1-3: the Collect leg now runs the SAME entry point the gun and
                                            the typed Enter use, so all three cannot diverge -- including the
                                            duplicate check, which this button never performed at all. */}
                                        <Button
                                            onClick={assets.find(a => a.id === newAssetId)?.collected ? uncollectAssetConfirm :
                                                () => runScanEntryRef.current({
                                                    serial: newAssetSerialNumber,
                                                    assetTag: newAssetAssetTag,
                                                    source: 'button'
                                                })
                                            }
                                            variant="contained"
                                            color={assets.find(a => a.id === newAssetId)?.collected ? 'warning' : 'primary'}
                                            /*
                                            disabled={!assets.some(asset => asset.serial_number === newAssetSerialNumber || asset.asset_tag === newAssetAssetTag)}

                                             */
                                        >
                                            {assets.find(a => a.id === newAssetId)?.collected ? 'Remove From Collection' : 'Collect'}
                                        </Button>

                                        <Button
                                            onClick={newAssetId == null
                                                ? () => saveNewAsset(false)
                                                : () => editAsset()
                                            }
                                            variant="contained"
                                            color="primary"
                                            disabled={false}
                                        >
                                            {newAssetId == null ? "Add Unexpected Asset" : "Edit Asset"}
                                        </Button>

                                    </>
                                ) : (
                                    <>
                                        <Button
                                            onClick={editAsset}
                                            variant="contained"
                                            color="primary"
                                            disabled={newAssetId <= 0}>
                                            Edit Asset
                                        </Button>
                                    </>
                                )}

                                <Button
                                    tabIndex={-1}
                                    onClick={() => emptyNewAssetFields()}
                                    variant="contained"
                                    color="primary"
                                    disabled={false}
                                >Cancel Scan
                                </Button>


                            </div>
                        </div>
                    </>
                ) : null}

                <StripedDataGrid
                    slots={{toolbar: GridToolbar}}
                    getRowClassName={(params) => {
                        const classes = [];
                        if (params.row.collected){
                            classes.push("collected");
                        } else {
                            classes.push("not-collected");
                        }

                        if( params.indexRelativeToCurrentPage % 2 === 0  ) {
                            classes.push("even");
                        } else {
                            classes.push("odd");
                        }

                        // Spec §2.2 requires an unexpected item's row to be highlighted YELLOW.
                        // It never was: adding an unexpected asset always collects it too, so
                        // `collected` won and the row rendered green like any matched item. The
                        // CSS gives this rule higher specificity than .collected.
                        if (params.row.unexpected === true) {
                            classes.push("unexpected");
                        }

                        // P1-3: the row a duplicate scan just collided with, so "already
                        // collected" is something the operator can see and not just be told.
                        // Compared as strings: this id can come from the redux row (a number)
                        // or from the server's duplicateOfId, and a type mismatch here would
                        // silently mean no highlight at all.
                        if (flashDuplicateId != null && String(params.row.id) === String(flashDuplicateId)) {
                            classes.push("duplicate-flash");
                        }

                        return classes.join(' ')
                     }
                    }
                    onRowSelectionModelChange={handleSelection}
                    rowSelectionModel={selectionModel}
                    //rows={assetsWithRelations}
                    rows={(() => {
                        if(dataFilter.top && dataFilter.top > 0){
                            const items = [...assetsWithRelations];
                            const itemToMove = items.find(item => item.id === dataFilter.top);
                            if (itemToMove) {
                                items.splice(items.indexOf(itemToMove), 1);
                                items.unshift(itemToMove);
                            }
                            return items;
                        }else{
                           return assetsWithRelations;
                        }
                    })()}

                    columns={columns}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 20,
                            },
                        },
                    }}
                    density="compact"
                    autoHeight={true}
                    fullWidth={true}
                    pageSizeOptions={pageSizeOptions}
                />
                {!isLoaded ? (
                    <div style={{position: "fixed", top:0, bottom: 0, left: 0, right:0, backgroundColor: "#00000022", display: "flex", justifyContent: "center", alignItems: "center"}}>
                        <div className="lds-roller">
                            <div></div>
                            <div></div>
                            <div></div>
                            <div></div>
                            <div></div>
                            <div></div>
                            <div></div>
                            <div></div>
                        </div>
                    </div>
                ) : null}
                {/* P1-3: the refusal notice.
                    This markup was already here, gated on `moreThanOneMatchMessage` — state that
                    was only ever set to `false`, so it had never once rendered. It is now the
                    single channel for every refused entry: duplicate, ambiguous, validation and
                    server error.

                    A blocking notice rather than the snackbar, because the snackbar auto-hides
                    after 6s and has one slot, so a following message replaces it. A duplicate the
                    operator never saw is precisely the 03/06/2026 symptom.

                    The OK button is deliberately NOT autoFocused: focusing it would route the
                    scan gun's keystrokes into a button instead of the document listener. The
                    notice also clears itself on the next accepted entry (see completeEntry), so a
                    driver can keep scanning without stopping to dismiss it. */}
                {blockingNotice ? (
                    <div style={{position: "fixed", top:0, bottom: 0, left: 0, right:0, backgroundColor: "#00000055", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1400}}>
                        <div style={{backgroundColor: "white", padding: 25, borderRadius: 5, display: "flex", flexDirection: "column", alignItems: "center", maxWidth: 520, borderTop: '6px solid #c62828'}}>
                            <div style={{fontWeight: 'bold', fontSize: 22, marginBottom: 10, color: '#c62828'}}>
                                {blockingNotice.title}
                            </div>
                            <div style={{marginBottom: blockingNotice.detail ? 6 : 20, textAlign: 'center'}}>
                                {blockingNotice.message}
                            </div>
                            {blockingNotice.detail ? (
                                <div style={{marginBottom: 20, textAlign: 'center', color: '#555', fontSize: 14}}>
                                    {blockingNotice.detail}
                                </div>
                            ) : null}
                            <Button onClick={() => setBlockingNotice(null)} style={{marginLeft: 10, marginTop:10, marginBottom: 10, width: 200, height: 50}} variant="contained" color="primary">OK</Button>
                        </div>
                    </div>
                ) : null}

                {/* P1-8: what the Duplicate tile counted. A list, not a grid filter — repeat
                    attempts on one row would make the tile's number disagree with the row count. */}
                {showDuplicateLog ? (
                    <Dialog open={true} onClose={() => setShowDuplicateLog(false)} maxWidth="sm" fullWidth={true}>
                        <DialogTitle>Duplicate scans refused this session</DialogTitle>
                        <DialogContent>
                            <DialogContentText style={{marginBottom: 10}}>
                                These scans were rejected because the item was already collected on this
                                order. None of them changed any count.
                            </DialogContentText>
                            <table style={{width: '100%', borderCollapse: 'collapse'}}>
                                <thead>
                                    <tr style={{textAlign: 'left', borderBottom: '1px solid #ddd'}}>
                                        <th style={{padding: 6}}>Serial</th>
                                        <th style={{padding: 6}}>Item #</th>
                                        <th style={{padding: 6}}>Time</th>
                                        <th style={{padding: 6}}>Source</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {duplicateEvents(duplicateTally, selectedCollection?.id).map((e, i) => (
                                        <tr key={`${e.at}-${i}`} style={{borderBottom: '1px solid #f0f0f0'}}>
                                            <td style={{padding: 6}}>{e.serial}</td>
                                            <td style={{padding: 6}}>{itemNumbers.get(e.assetId) ?? '—'}</td>
                                            <td style={{padding: 6}}>{e.at ? new Date(e.at).toLocaleTimeString() : ''}</td>
                                            <td style={{padding: 6}}>{e.source || ''}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setShowDuplicateLog(false)} variant="contained">Close</Button>
                        </DialogActions>
                    </Dialog>
                ) : null}


                {openSnackbar ?
                    <Snackbar  open={true} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} autoHideDuration={6000}>
                        <Alert
                            onClose={handleSnackbarClose}
                            severity={openSnackbar.status}
                            sx={{ width: '100%' }} >
                            {openSnackbar.message}
                        </Alert>
                    </Snackbar>
                    : null}


                <Dialog
                    open={confirm.open}
                    onClose={handleConfirmClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description">

                    <DialogTitle id="alert-dialog-title">
                        {confirm.title}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            {confirm.msg}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions style={{paddingBottom: 20, paddingRight: 20}}>
                        <Button onClick={handleConfirmClose}>No! Cancel </Button>
                        <Button variant="contained" onClick={() => confirm.action()} color="primary" autoFocus>{confirm.btn_msg}</Button>
                    </DialogActions>
                </Dialog>


            </Box>

        </>
    )
}

export default AssetManagement;

